import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService, graphBuilderApi } from '../../services/api';
import BuildGraphMenu from './BuildGraphMenu';
import GraphPromptsModal from './GraphPromptsModal';
import ChunkModal from './ChunkModal';
import ReprocessConfirmModal from './ReprocessConfirmModal';
import DocumentPromptModal from './DocumentPromptModal';
import ProcessingSettingsPanel, { BUILD_APPROACH_OPTIONS, CHUNKING_STRATEGY_OPTIONS } from './ProcessingSettingsPanel';
import './BuildGraph.css';

// Default processing parameters
const DEFAULT_PARAMS = {
  build_approach: 'composite_parallel',
  max_iterations_per_chunk: 2,
  chunk_retries: 2,
  model: 'gpt-4o',
  max_words_per_chunk: 250,  // Word-based chunking, ends at sentence boundary
  chunking_strategy: 'word_based',  // 'word_based' or 'structure_aware'
  hard_limit_words: 800,  // Only used for structure_aware strategy
  // Coverage validation settings
  enable_coverage_validation: true,
  min_coverage_threshold: 70,  // Minimum coverage % before re-extraction
  enable_reextraction: true,   // Whether to re-extract from low-coverage areas
  // Fast Graph Engine knobs (precision atomizer + lossless coverage).
  // Forwarded to GraphBuilder POST /api/build_precision_graph/submit when
  // build_approach === 'fast_graph_engine_precision'.
  fge_llm_budget: 150,
  fge_coverage_threshold: 70,
  fge_extract_images: true,
  fge_vision_model: 'gpt-4o',
  fge_enable_xref: true,
  fge_enable_coref: true,
  fge_enable_tables: true,
  fge_enable_vocab_forge: true,
  fge_enable_typer_v2: true,
  fge_enable_domain_ontology: true,
};

const STATIC_MODEL_OPTIONS = [
  { value: 'gpt-4o', label: 'GPT-4o (Default)' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Faster)' },
  { value: 'gpt-5-mini', label: 'GPT-5 Mini' },
  { value: 'gpt-5.2', label: 'GPT-5.2' },
  { value: 'gpt-5.2-mini', label: 'GPT-5.2 Mini' },
];

function paramsFromDocument(processingParams) {
  const next = { ...DEFAULT_PARAMS };
  if (!processingParams || typeof processingParams !== 'object') return next;
  Object.keys(DEFAULT_PARAMS).forEach((key) => {
    if (processingParams[key] !== undefined && processingParams[key] !== null) {
      next[key] = processingParams[key];
    }
  });
  if (processingParams.auto_generate_prompt !== undefined && processingParams.auto_generate_prompt !== null) {
    next.auto_generate_prompt = processingParams.auto_generate_prompt;
  }
  const providerId = processingParams.provider_id;
  const model = next.model;
  if (providerId && model && !String(model).startsWith('provider::')) {
    next.model = `provider::${providerId}::${model}`;
  }
  return next;
}

const PROVIDER_TYPE_LABELS = {
  openai: 'OpenAI',
  do_serverless: 'DO Serverless',
  do_droplet: 'DO Droplet',
  do_dedicated: 'DO Dedicated',
  runpod_pod: 'GPU Pod',
  runpod_serverless: 'RunPod Serverless',
};

// BUILD_APPROACH_OPTIONS and CHUNKING_STRATEGY_OPTIONS imported from ProcessingSettingsPanel

// Map MIME types to friendly file extensions
const getMimeExtension = (mimeType) => {
  const map = {
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
    'application/msword': '.doc',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.ms-powerpoint': '.ppt',
    'application/pdf': '.pdf',
    'text/plain': '.txt',
    'text/csv': '.csv',
    'application/json': '.json',
    'text/html': '.html',
    'application/xml': '.xml',
    'image/png': '.png',
    'image/jpeg': '.jpg',
  };
  return map[mimeType?.toLowerCase()] || ('.' + (mimeType?.split('/')[1] || 'unknown'));
};

/**
 * RouterConfigPanel - Manages target graphs and chunk generation for Router graphs.
 * Shown instead of the document upload section when graphType === 'Router'.
 */
function RouterConfigPanel({ graphId, onChunksGenerated, processingParams }) {
  const [targets, setTargets] = useState([]);
  const [availableGraphs, setAvailableGraphs] = useState([]);
  const [availableProcesses, setAvailableProcesses] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [addMode, setAddMode] = useState('graph');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [editingMarkers, setEditingMarkers] = useState(null);
  const [markersText, setMarkersText] = useState('');
  const [error, setError] = useState(null);

  const loadTargets = useCallback(async () => {
    try {
      const res = await apiService.getRouterTargets(graphId);
      setTargets(res.targets || []);
    } catch (err) {
      console.error('Failed to load router targets:', err);
      setError(prev => prev || `Failed to load router targets: ${err.response?.data?.error || err.message}`);
    }
  }, [graphId]);

  const loadAvailableGraphs = useCallback(async () => {
    try {
      const res = await apiService.listGraphs(1, 100);
      const graphs = (res.graphs || []).filter(
        g => g.graphType !== 'Router' && g.graphId !== graphId
      );
      setAvailableGraphs(graphs);
    } catch (err) {
      console.error('Failed to load available graphs:', err);
      setError(`Failed to load available graphs: ${err.response?.data?.error || err.message}`);
    }
  }, [graphId]);

  const loadAvailableProcesses = useCallback(async () => {
    try {
      const res = await graphBuilderApi.listProcessDefinitions({ status: 'active' });
      setAvailableProcesses(Array.isArray(res) ? res : (res.definitions || []));
    } catch (err) {
      console.error('Failed to load process definitions:', err);
    }
  }, []);

  const reloadAll = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([loadTargets(), loadAvailableGraphs(), loadAvailableProcesses()]).finally(() => setLoading(false));
  }, [loadTargets, loadAvailableGraphs, loadAvailableProcesses]);

  useEffect(() => {
    reloadAll();
  }, [reloadAll]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddTarget = async () => {
    if (selectedIds.size === 0) return;
    setError(null);
    try {
      await apiService.addRouterTargets(graphId, [...selectedIds], { targetType: addMode });
      setSelectedIds(new Set());
      setDropdownOpen(false);
      await Promise.all([loadTargets(), loadAvailableGraphs()]);
    } catch (err) {
      console.error('Failed to add target:', err);
      setError(`Failed to add target: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleRemoveTarget = async (targetId) => {
    setError(null);
    try {
      await apiService.removeRouterTarget(graphId, targetId);
      await loadTargets();
    } catch (err) {
      console.error('Failed to remove target:', err);
      setError(`Failed to remove target: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleSaveMarkers = async () => {
    if (!editingMarkers) return;
    setError(null);
    try {
      await apiService.updateRouterTargetMarkers(graphId, editingMarkers, markersText);
      setEditingMarkers(null);
      setMarkersText('');
      await loadTargets();
    } catch (err) {
      console.error('Failed to save markers:', err);
      setError(`Failed to save markers: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleGenerateDocument = async () => {
    setGenerating(true);
    setGenResult(null);
    setError(null);
    try {
      const result = await apiService.generateRouterChunks(graphId, processingParams);
      setGenResult(result);
      if (onChunksGenerated) onChunksGenerated();
    } catch (err) {
      setGenResult({ errors: [err.message || 'Generation failed'] });
    } finally {
      setGenerating(false);
    }
  };

  const getTargetId = (t) => t.target_graph_id || t.target_process_id;
  const alreadyAddedGraphs = new Set(targets.filter(t => t.target_type !== 'process').map(t => t.target_graph_id));
  const alreadyAddedProcesses = new Set(targets.filter(t => t.target_type === 'process').map(t => t.target_process_id));
  const filteredAvailableGraphs = availableGraphs.filter(g => !alreadyAddedGraphs.has(g.graphId));
  const filteredAvailableProcesses = availableProcesses.filter(p => !alreadyAddedProcesses.has(p.process_id));

  if (loading) return <div className="loading-spinner">Loading router configuration...</div>;

  return (
    <div className="upload-section">
      <h2>Router Configuration</h2>
      <p className="section-description">
        Select target graphs or process maps to include in this router. The system will generate a document from
        each target's description, Q&A pairs, vocabulary, and your custom query markers.
      </p>

      {error && (
        <div style={{ padding: '12px 16px', marginBottom: '16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <button className="btn btn-secondary" onClick={reloadAll} style={{ marginLeft: '12px', whiteSpace: 'nowrap' }}>
            Retry
          </button>
        </div>
      )}

      {/* Target Type Toggle */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '12px' }}>
        <button
          className={`btn btn-sm ${addMode === 'graph' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setAddMode('graph'); setSelectedIds(new Set()); setDropdownOpen(false); }}
          style={{ borderRadius: '6px 0 0 6px' }}
        >
          Graph
        </button>
        <button
          className={`btn btn-sm ${addMode === 'process' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setAddMode('process'); setSelectedIds(new Set()); setDropdownOpen(false); }}
          style={{ borderRadius: '0 6px 6px 0' }}
        >
          Process Map
        </button>
      </div>

      {/* Add Target - multi-select dropdown */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>
        <div ref={dropdownRef} style={{ flex: 1, position: 'relative' }}>
          <button
            type="button"
            onClick={() => setDropdownOpen(o => !o)}
            style={{
              width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db',
              background: '#fff', textAlign: 'left', cursor: 'pointer', display: 'flex',
              justifyContent: 'space-between', alignItems: 'center', fontSize: '14px', color: selectedIds.size > 0 ? '#111827' : '#6b7280',
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedIds.size === 0
                ? (addMode === 'graph'
                  ? (filteredAvailableGraphs.length === 0 ? 'No available graphs found' : 'Select graphs to add...')
                  : (filteredAvailableProcesses.length === 0 ? 'No active process maps found' : 'Select process maps to add...'))
                : `${selectedIds.size} selected`}
            </span>
            <span style={{ marginLeft: '8px', fontSize: '10px' }}>{dropdownOpen ? '\u25B2' : '\u25BC'}</span>
          </button>
          {dropdownOpen && (() => {
            const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
            const items = addMode === 'graph'
              ? filteredAvailableGraphs.map(g => ({ id: g.graphId, label: `${g.graphName} (${g.graphType}) - ${g.description?.substring(0, 60) || 'No description'}`, created: fmtDate(g.createDate), modified: fmtDate(g.changeDate) }))
              : filteredAvailableProcesses.map(p => ({ id: p.process_id, label: `${p.name} - ${p.description?.substring(0, 60) || 'No description'}`, created: fmtDate(p.created_at), modified: fmtDate(p.updated_at) }));
            const toggleId = (id) => {
              setSelectedIds(prev => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id); else next.add(id);
                return next;
              });
            };
            const allSelected = items.length > 0 && items.every(i => selectedIds.has(i.id));
            const toggleAll = () => {
              if (allSelected) setSelectedIds(new Set());
              else setSelectedIds(new Set(items.map(i => i.id)));
            };
            return (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px',
                marginTop: '4px', maxHeight: '260px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}>
                {items.length === 0 ? (
                  <div style={{ padding: '12px', color: '#6b7280', textAlign: 'center' }}>
                    {addMode === 'graph' ? 'No available graphs' : 'No active process maps'}
                  </div>
                ) : (
                  <>
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
                      cursor: 'pointer', borderBottom: '1px solid #e5e7eb', fontWeight: 600, fontSize: '13px',
                    }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                      Select All ({items.length})
                    </label>
                    {items.map(item => (
                      <label key={item.id} style={{
                        display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '6px 12px',
                        cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f3f4f6',
                      }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => toggleId(item.id)} style={{ marginTop: '3px' }} />
                        <div style={{ overflow: 'hidden', minWidth: 0 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</div>
                          {(item.created || item.modified) && (
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '1px' }}>
                              {item.created && `Created: ${item.created}`}{item.created && item.modified && ' · '}{item.modified && `Modified: ${item.modified}`}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </>
                )}
              </div>
            );
          })()}
        </div>
        <button
          className="btn btn-primary"
          onClick={handleAddTarget}
          disabled={selectedIds.size === 0}
          style={{ whiteSpace: 'nowrap' }}
        >
          {addMode === 'graph'
            ? (selectedIds.size > 1 ? `Add ${selectedIds.size} Graphs` : 'Add Graph')
            : (selectedIds.size > 1 ? `Add ${selectedIds.size} Processes` : 'Add Process')}
        </button>
      </div>

      {/* Targets List */}
      {targets.length === 0 ? (
        <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', border: '1px dashed #d1d5db', borderRadius: '8px' }}>
          No targets added yet. Add graphs or process maps above to build this router.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
          {targets.map(t => {
            const tId = getTargetId(t);
            const isProcess = t.target_type === 'process';
            const editKey = tId;
            return (
              <div key={t.id} style={{
                padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: '8px',
                backgroundColor: '#f9fafb',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{
                      display: 'inline-block', padding: '1px 6px', borderRadius: '4px', fontSize: '10px',
                      fontWeight: 600, marginRight: '8px', textTransform: 'uppercase',
                      background: isProcess ? '#fef3c7' : '#dbeafe',
                      color: isProcess ? '#92400e' : '#1d4ed8',
                      border: `1px solid ${isProcess ? '#fcd34d' : '#93c5fd'}`,
                    }}>
                      {isProcess ? 'Process' : 'Graph'}
                    </span>
                    <strong>{t.target_name || t.target_graph_name}</strong>
                    <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6b7280' }}>
                      {t.target_status || ''}
                    </span>
                    <div style={{ fontSize: '13px', color: '#4b5563', marginTop: '2px' }}>
                      {(t.target_description || t.target_graph_description || '')?.substring(0, 120) || 'No description'}
                    </div>
                    {t.query_markers && (
                      <div style={{ fontSize: '12px', color: '#059669', marginTop: '4px' }}>
                        Query markers configured
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => {
                        setEditingMarkers(editKey);
                        setMarkersText(t.query_markers || '');
                      }}
                      title="Edit query markers"
                    >
                      Markers
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleRemoveTarget(tId)}
                      title="Remove from router"
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {/* Inline markers editor */}
                {editingMarkers === editKey && (
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e5e7eb' }}>
                    <label style={{ fontSize: '13px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>
                      Query Identification Markers
                    </label>
                    <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
                      Enter example queries, keywords, or phrases that indicate a user query should be routed to this target. One per line.
                    </p>
                    <textarea
                      rows={4}
                      value={markersText}
                      onChange={(e) => setMarkersText(e.target.value)}
                      placeholder="What is my insurance coverage?\nHow do I file a claim?\ncoverage, deductible, premium"
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}
                    />
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                      <button className="btn btn-sm btn-primary" onClick={handleSaveMarkers}>Save</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setEditingMarkers(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Generate Router Document Button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
        <button
          className="btn btn-primary"
          onClick={handleGenerateDocument}
          disabled={generating || targets.length === 0}
        >
          {generating ? 'Generating Document...' : 'Generate Router Document'}
        </button>
        <span style={{ fontSize: '13px', color: '#6b7280' }}>
          {targets.length} target{targets.length !== 1 ? 's' : ''} configured
        </span>
      </div>

      {/* Generation Result */}
      {genResult && (
        <div style={{
          marginTop: '12px', padding: '12px', borderRadius: '8px',
          backgroundColor: genResult.errors?.length ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${genResult.errors?.length ? '#fecaca' : '#bbf7d0'}`,
        }}>
          <div style={{ fontWeight: 500, marginBottom: '4px' }}>
            Router document created from {genResult.targets_processed || 0} target{(genResult.targets_processed || 0) !== 1 ? 's' : ''}
          </div>
          {genResult.errors?.length > 0 && (
            <div style={{ fontSize: '13px', color: '#dc2626' }}>
              {genResult.errors.map((err, i) => <div key={i}>{err}</div>)}
            </div>
          )}
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            The document is now in the documents list below. Process it to build the router graph.
          </p>
        </div>
      )}
    </div>
  );
}


function BuildGraphPage() {
  const { graphId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Document states
  const [documents, setDocuments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationErrors, setValidationErrors] = useState([]);
  const [processStatus, setProcessStatus] = useState({ message: '', isError: false });
  
  // Processing settings state - persist to localStorage
  const [globalParams, setGlobalParams] = useState(() => {
    // Try to load from localStorage on initial render
    const saved = localStorage.getItem('graphBuilder_globalParams');
    if (saved) {
      try {
        return { ...DEFAULT_PARAMS, ...JSON.parse(saved) };
      } catch (e) {
        console.warn('Failed to parse saved globalParams:', e);
      }
    }
    return { ...DEFAULT_PARAMS };
  });
  const [showSettings, setShowSettings] = useState(true);
  const [expandedDocId, setExpandedDocId] = useState(null);
  const [processingAll, setProcessingAll] = useState(false);
  
  // Output modal state
  const [showOutputModal, setShowOutputModal] = useState(false);
  const [selectedOutput, setSelectedOutput] = useState(null);
  const [loadingOutput, setLoadingOutput] = useState(false);
  
  // Config
  const [config, setConfig] = useState({
    maxDocumentSizeMB: 50,
    maxDocumentsPerUpload: 10,
    allowedDocumentTypes: ['pdf', 'docx', 'txt', 'doc']
  });
  
  // LLM Providers state
  const [llmProviders, setLlmProviders] = useState([]);
  const modelOptions = React.useMemo(() => {
    // --- STATIC FALLBACK DISABLED: only DB-configured providers are shown ---
    // if (!llmProviders.length) return STATIC_MODEL_OPTIONS;
    const providerOpts = llmProviders.map((p) => ({
      value: `provider::${p.id}::${p.model_name}`,
      label: `${p.model_name} (${PROVIDER_TYPE_LABELS[p.provider_type] || p.provider_type}${p.name ? ' - ' + p.name : ''})`,
      provider_id: p.id,
      model_name: p.model_name,
      is_default: p.is_default,
    }));
    return providerOpts;
    // --- STATIC FALLBACK DISABLED ---
    // return [...providerOpts, ...STATIC_MODEL_OPTIONS];
  }, [llmProviders]);

  // Resolve provider::ID::model format for API calls
  const resolveParamsForApi = React.useCallback((params) => {
    if (!params?.model?.startsWith('provider::')) return params;
    const parts = params.model.split('::');
    return { ...params, model: parts[2], provider_id: parts[1] };
  }, []);

  // FGE (precision pipeline) re-chunks the source file inside GraphBuilder and ignores
  // composite-only knobs (Min Coverage %, Re-extract, Max Iterations, Chunk Retries,
  // saved DB chunks). Use this flag to hide controls that don't apply in FGE mode.
  const isFge = ['fast_graph_engine_precision', 'fge_v4', 'fge_v5', 'fge_v6', 'fge_v7', 'fge_v8', 'fge_v9', 'fge_v10', 'excel_graph_builder'].includes(globalParams.build_approach);

  // Document settings win over the saved bulk-build choice. A programmatic
  // update sets this so the open-page load is not written back to the document.
  const skipAutoApply = useRef(false);
  const seededGraphId = useRef(null);

  // Auto-select the default provider on mount if no provider-backed model is already saved.
  // Skip once the open graph's document has supplied the model.
  React.useEffect(() => {
    if (!llmProviders.length) return;
    if (seededGraphId.current === graphId) return;
    const defaultProvider = llmProviders.find((p) => p.is_default) || llmProviders[0];
    if (defaultProvider && !globalParams.model?.startsWith('provider::')) {
      skipAutoApply.current = true;
      setGlobalParams((prev) => ({
        ...prev,
        model: `provider::${defaultProvider.id}::${defaultProvider.model_name}`,
      }));
    }
  }, [llmProviders, graphId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drag and drop state
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  // Consolidation state
  const [consolidating, setConsolidating] = useState(false);
  const [consolidationId, setConsolidationId] = useState(null);
  const [consolidationStatus, setConsolidationStatus] = useState(null);
  const [consolidationHistory, setConsolidationHistory] = useState([]);
  const [reviewQueueCount, setReviewQueueCount] = useState(0);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showConsolidationDetails, setShowConsolidationDetails] = useState(false);
  const [reviewItems, setReviewItems] = useState([]);
  const [loadingReview, setLoadingReview] = useState(false);
  const [selectedDocsForConsolidation, setSelectedDocsForConsolidation] = useState(new Set());
  
  // Graph Prompts state
  const [showPromptsModal, setShowPromptsModal] = useState(false);
  const [promptsStatus, setPromptsStatus] = useState({
    has_graph_customizations: false,
    has_document_overrides: false,
  });
  
  // Chunk Management state
  const [showChunkModal, setShowChunkModal] = useState(false);
  const [chunkCounts, setChunkCounts] = useState(null);
  const [chunkingDocId, setChunkingDocId] = useState(null);
  const [processingPendingChunks, setProcessingPendingChunks] = useState(false);
  
  // Per-document prompt generation state
  const [generatingPromptDocId, setGeneratingPromptDocId] = useState(null);
  
  // Document prompt view/edit modal state
  const [promptModalDocId, setPromptModalDocId] = useState(null);
  const [promptModalDocName, setPromptModalDocName] = useState('');
  
  // Reprocess state
  const [showReprocessModal, setShowReprocessModal] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  
  // Resume state
  const [resuming, setResuming] = useState(false);
  
  // Re-persist state (for documents where build succeeded but DB persist failed)
  const [repersistingDocId, setRepersistingDocId] = useState(null);
  
  // Track if initial mount is complete (to avoid auto-apply on page load)
  const isInitialMount = useRef(true);
  const prevGlobalParams = useRef(globalParams);
  const documentsRef = useRef(documents);
  
  // Keep documentsRef in sync
  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  // Persist globalParams to localStorage and auto-apply to unprocessed documents
  useEffect(() => {
    localStorage.setItem('graphBuilder_globalParams', JSON.stringify(globalParams));
    
    // Skip auto-apply on initial mount, when adopting the document's saved
    // settings, and when filling in the default provider.
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevGlobalParams.current = globalParams;
      return;
    }
    if (skipAutoApply.current) {
      skipAutoApply.current = false;
      prevGlobalParams.current = globalParams;
      return;
    }
    
    // Check if settings actually changed (not just re-render)
    const paramsChanged = JSON.stringify(prevGlobalParams.current) !== JSON.stringify(globalParams);
    prevGlobalParams.current = globalParams;
    
    if (!paramsChanged || !graphId) return;
    
    // Auto-apply global settings to all unprocessed documents
    const applyGlobalToUnprocessed = async () => {
      // Check if there are any unprocessed documents (use ref to avoid dependency)
      const currentDocs = documentsRef.current;
      const unprocessedDocs = currentDocs.filter(d => ['uploaded', 'queued'].includes(d.status));
      if (unprocessedDocs.length === 0) return;
      
      try {
        const response = await apiService.updateGraphDocumentsParams(graphId, resolveParamsForApi(globalParams));
        if (response.success && response.updated_count > 0) {
          console.log(`Auto-applied global settings to ${response.updated_count} document(s)`);
          // Update local state to reflect changes immediately
          setDocuments(prevDocs => prevDocs.map(doc => {
            if (['uploaded', 'queued'].includes(doc.status)) {
              return { ...doc, processing_params: { ...doc.processing_params, ...globalParams } };
            }
            return doc;
          }));
        }
      } catch (err) {
        console.error('Error auto-applying global settings:', err);
      }
    };
    
    applyGlobalToUnprocessed();
  }, [globalParams, graphId]); // Removed documents from deps to avoid loops

  const loadGraphDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getGraphDetails(graphId);
      if (response.success) {
        setGraph(response.graph);
      } else {
        setError('Failed to load graph details');
      }
    } catch (err) {
      console.error('Error loading graph:', err);
      setError(err.response?.data?.message || 'Failed to load graph details');
    } finally {
      setLoading(false);
    }
  }, [graphId]);

  const loadUploadConfig = useCallback(async () => {
    try {
      const response = await apiService.getDocumentUploadConfig();
      if (response.success) {
        setConfig(response.config);
      }
    } catch (err) {
      console.error('Error loading config:', err);
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    try {
      const response = await apiService.getGraphDocuments(graphId);
      if (response.success) {
        const docs = response.documents || [];
        setDocuments(docs);
        if (seededGraphId.current !== graphId) {
          const source = docs.find((doc) => doc?.processing_params?.build_approach);
          if (source) {
            seededGraphId.current = graphId;
            skipAutoApply.current = true;
            setGlobalParams(paramsFromDocument(source.processing_params));
          }
        }
        
        // Auto-select newly processed documents that haven't been consolidated yet
        // (Documents that are 'processed' but not yet part of any consolidation)
        setSelectedDocsForConsolidation(prev => {
          const newSelection = new Set(prev);
          docs.forEach(doc => {
            // Auto-select processed documents by default
            if (doc.status === 'processed' && !prev.has(doc.document_id)) {
              newSelection.add(doc.document_id);
            }
          });
          return newSelection;
        });
      }
    } catch (err) {
      console.error('Error loading documents:', err);
    }
  }, [graphId]);

  // Toggle document selection for consolidation
  const toggleDocForConsolidation = (docId) => {
    setSelectedDocsForConsolidation(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  // Select all processed documents
  const selectAllProcessedDocs = () => {
    const processedDocIds = documents
      .filter(doc => doc.status === 'processed')
      .map(doc => doc.document_id);
    setSelectedDocsForConsolidation(new Set(processedDocIds));
  };

  // Deselect all documents
  const deselectAllDocs = () => {
    setSelectedDocsForConsolidation(new Set());
  };

  // Get count of selected processed documents
  const selectedProcessedCount = documents.filter(
    doc => doc.status === 'processed' && selectedDocsForConsolidation.has(doc.document_id)
  ).length;

  // Load review queue count
  const loadReviewQueueCount = useCallback(async () => {
    try {
      const response = await graphBuilderApi.getReviewQueueCount(graphId);
      setReviewQueueCount(response.count || 0);
    } catch (err) {
      console.error('Error loading review count:', err);
    }
  }, [graphId]);

  // Load consolidation history
  const loadConsolidationHistory = useCallback(async () => {
    try {
      const response = await graphBuilderApi.getConsolidationHistory(graphId, 5);
      setConsolidationHistory(response.history || []);
    } catch (err) {
      console.error('Error loading consolidation history:', err);
    }
  }, [graphId]);

  // Polling function for consolidation status - defined first so it can be used by checkActiveConsolidation
  const pollConsolidationStatus = useCallback((consId) => {
    let attempts = 0;
    const maxAttempts = 180; // 15 minutes with 5s interval
    
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setConsolidating(false);
        setConsolidationStatus({ status: 'timeout', message: 'Consolidation timed out - check logs for status' });
        return;
      }

      try {
        const status = await graphBuilderApi.getConsolidationStatus(graphId, consId);
        setConsolidationStatus(status);

        if (status.status === 'completed') {
          setConsolidating(false);
          setSuccess(`Consolidation completed! ${formatConsolidationSummary(status.summary)}`);
          loadReviewQueueCount();
          loadConsolidationHistory();
        } else if (status.status === 'failed') {
          setConsolidating(false);
          setProcessStatus({ message: status.error_message || 'Consolidation failed', isError: true });
        } else {
          // Still running, poll again
          attempts++;
          setTimeout(poll, 5000);
        }
      } catch (err) {
        // 404 means job not in DB yet (still running in background worker)
        console.log('[Consolidation] Status poll attempt', attempts + 1, '- still running...');
        setConsolidationStatus({ 
          status: 'running', 
          message: `Analyzing graph... (${Math.floor(attempts * 5 / 60)}m ${(attempts * 5) % 60}s elapsed)` 
        });
        attempts++;
        setTimeout(poll, 5000);
      }
    };

    poll();
  }, [graphId, loadReviewQueueCount, loadConsolidationHistory]);

  // Helper function to format consolidation summary
  const formatConsolidationSummary = (summary) => {
    if (!summary) return '';
    const auto = summary.auto_applied || {};
    const warning = summary.applied_with_warning || {};
    const review = summary.queued_for_review || {};
    
    const parts = [];
    const autoTotal = (auto.merges || 0) + (auto.relationships || 0) + (auto.gates || 0);
    const warningTotal = (warning.merges || 0) + (warning.relationships || 0) + (warning.gates || 0);
    const reviewTotal = (review.merges || 0) + (review.relationships || 0) + (review.gates || 0);
    
    if (autoTotal > 0) parts.push(`${autoTotal} auto-applied`);
    if (warningTotal > 0) parts.push(`${warningTotal} with warnings`);
    if (reviewTotal > 0) parts.push(`${reviewTotal} for review`);
    
    return parts.length > 0 ? parts.join(', ') : 'No changes needed';
  };

  // Check for active consolidation on page load
  const checkActiveConsolidation = useCallback(async () => {
    try {
      const response = await graphBuilderApi.getActiveConsolidation(graphId);
      if (response.active && response.consolidation_id) {
        console.log('[Consolidation] Found active consolidation:', response.consolidation_id);
        setConsolidating(true);
        setConsolidationId(response.consolidation_id);
        setConsolidationStatus({ 
          status: response.status || 'running', 
          message: response.progress || 'Consolidation in progress...' 
        });
        // Start polling for this active consolidation
        pollConsolidationStatus(response.consolidation_id);
      }
    } catch (err) {
      console.error('Error checking active consolidation:', err);
    }
  }, [graphId, pollConsolidationStatus]);

  // Handle consolidate graph
  const handleConsolidateGraph = async () => {
    console.log('[Consolidation] Starting...', { graphId, workspaceId: graph?.workspaceId });
    
    if (!graph?.workspaceId) {
      console.error('[Consolidation] Missing workspace ID');
      setProcessStatus({ message: 'Missing workspace ID', isError: true });
      return;
    }

    // Get selected document IDs (only processed documents)
    const selectedDocIds = documents
      .filter(doc => doc.status === 'processed' && selectedDocsForConsolidation.has(doc.document_id))
      .map(doc => doc.document_id);

    if (selectedDocIds.length === 0) {
      setProcessStatus({ message: 'Please select at least one processed document to consolidate', isError: true });
      return;
    }

    setConsolidating(true);
    setConsolidationStatus({ status: 'starting', message: `Starting consolidation with ${selectedDocIds.length} document(s)...` });
    setError(null);
    setSuccess(null);

    try {
      console.log('[Consolidation] Calling API with documents:', selectedDocIds);
      const response = await graphBuilderApi.consolidateGraph(graphId, graph.workspaceId, null, selectedDocIds);
      console.log('[Consolidation] API Response:', response);
      
      setConsolidationId(response.consolidation_id);
      
      // Check if this is an existing consolidation in progress
      const isExisting = response.summary?.message === 'Consolidation already in progress';
      if (isExisting) {
        setConsolidationStatus({ status: 'running', message: 'Found active consolidation, tracking progress...' });
        setSuccess('Tracking existing consolidation...');
      } else {
        setConsolidationStatus({ status: 'running', message: 'Consolidation in progress...' });
        setSuccess('Consolidation started! Analyzing graph...');
      }

      // Poll for status
      pollConsolidationStatus(response.consolidation_id);
    } catch (err) {
      console.error('[Consolidation] Error:', err);
      setConsolidating(false);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to start consolidation';
      setConsolidationStatus({ status: 'failed', message: errorMsg });
      setError(`Consolidation failed: ${errorMsg}`);
    }
  };

  // Load review queue items
  const loadReviewItems = async () => {
    setLoadingReview(true);
    try {
      const response = await graphBuilderApi.getReviewQueue(graphId);
      setReviewItems(response.items || []);
    } catch (err) {
      console.error('Error loading review items:', err);
    } finally {
      setLoadingReview(false);
    }
  };

  // Handle review action
  const handleReviewAction = async (reviewId, action) => {
    try {
      await graphBuilderApi.processReviewItem(graphId, reviewId, action);
      // Remove from list
      setReviewItems(prev => prev.filter(item => item.review_id !== reviewId));
      setReviewQueueCount(prev => Math.max(0, prev - 1));
      setSuccess(`Item ${action}d successfully`);
    } catch (err) {
      console.error('Review action error:', err);
      setProcessStatus({ message: 'Failed to process review item', isError: true });
    }
  };

  // Load prompts customization status
  const loadPromptsStatus = useCallback(async () => {
    try {
      const response = await apiService.getGraphPromptsStatus(graphId);
      if (response.success) {
        setPromptsStatus({
          has_graph_customizations: response.has_graph_customizations || false,
          has_document_overrides: response.has_document_overrides || false,
        });
      }
    } catch (err) {
      console.error('Error loading prompts status:', err);
    }
  }, [graphId]);

  // Load chunk counts for the graph
  const loadChunkCounts = useCallback(async () => {
    try {
      const response = await apiService.getChunkCounts(graphId);
      if (response.success) {
        setChunkCounts(response.counts);
      }
    } catch (err) {
      console.error('Error loading chunk counts:', err);
    }
  }, [graphId]);

  // Handle graph reprocessing
  const handleReprocessGraph = async (confirmationCode) => {
    if (!graphId) return;
    
    setReprocessing(true);
    setError(null);
    setSuccess(null);
    
    try {
      console.log('[Reprocess] Starting reprocess for graph:', graphId);
      const response = await apiService.reprocessGraph(graphId, confirmationCode);
      
      if (response.success) {
        console.log('[Reprocess] Success:', response);
        setShowReprocessModal(false);
        
        // Show success message with details
        const details = [];
        if (response.deleted?.nodes) details.push(`${response.deleted.nodes} nodes`);
        if (response.deleted?.synapses) details.push(`${response.deleted.synapses} synapses`);
        if (response.deleted?.qa) details.push(`${response.deleted.qa} Q/A`);
        
        const detailStr = details.length > 0 ? ` (deleted: ${details.join(', ')})` : '';
        setSuccess(`Graph cleared for reprocessing${detailStr}. ${response.chunks_reset || 0} chunks reset to pending.`);
        
        // Reload documents and graph data
        loadDocuments();
        loadGraphDetails();
        loadChunkCounts();
        loadConsolidationHistory();
      } else {
        setError(response.message || 'Failed to reprocess graph');
      }
    } catch (err) {
      console.error('[Reprocess] Error:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to reprocess graph';
      setError(`Reprocess failed: ${errorMsg}`);
    } finally {
      setReprocessing(false);
    }
  };

  // Handle resume graph processing from last checkpoint
  const handleResumeProcessing = async () => {
    if (!graphId) return;
    
    setResuming(true);
    setError(null);
    setSuccess(null);
    
    try {
      console.log('[Resume] Starting resume for graph:', graphId);
      const response = await apiService.resumeGraphProcessing(graphId);
      
      if (response.success) {
        console.log('[Resume] Success:', response);
        setSuccess(
          `Processing resumed from chunk ${response.resumed_from_chunk} of ${response.total_chunks}. ` +
          `Job ID: ${response.job_id}`
        );
        // Reload documents to see updated status
        loadDocuments();
      } else {
        setError(response.message || 'Failed to resume processing');
      }
    } catch (err) {
      console.error('[Resume] Error:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to resume processing';
      setError(`Resume failed: ${errorMsg}`);
    } finally {
      setResuming(false);
    }
  };

  // Load graph details and configuration (all calls are independent, run in parallel)
  useEffect(() => {
    Promise.allSettled([
      loadGraphDetails(),
      loadUploadConfig(),
      loadDocuments(),
      loadReviewQueueCount(),
      loadConsolidationHistory(),
      checkActiveConsolidation(),
      loadPromptsStatus(),
      loadChunkCounts(),
      apiService.listLLMProviders().then((d) => setLlmProviders(d.providers || [])).catch(() => {}),
    ]);
  }, [graphId, loadGraphDetails, loadUploadConfig, loadDocuments, loadReviewQueueCount, loadConsolidationHistory, checkActiveConsolidation, loadPromptsStatus, loadChunkCounts]);

  // Poll for updates while any document is processing
  useEffect(() => {
    const hasProcessing = documents.some(d => d.status === 'processing');
    if (!hasProcessing) return;

    const pollInterval = setInterval(() => {
      loadDocuments();
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [documents, loadDocuments]);

  const handleBack = () => {
    navigate('/user-home/graphs');
  };

  // File validation
  const validateFiles = (files) => {
    const errors = [];
    let totalSize = 0;

    if (files.length > config.maxDocumentsPerUpload) {
      errors.push(`Maximum ${config.maxDocumentsPerUpload} files allowed`);
      return { valid: false, errors };
    }

    files.forEach((file, index) => {
      const ext = file.name.split('.').pop().toLowerCase();
      
      if (!config.allowedDocumentTypes.includes(ext)) {
        errors.push(`File ${index + 1} (${file.name}): Invalid file type. Allowed: ${config.allowedDocumentTypes.join(', ')}`);
      }
      
      totalSize += file.size;
    });

    const maxBytes = config.maxDocumentSizeMB * 1024 * 1024;
    if (totalSize > maxBytes) {
      errors.push(`Total file size (${(totalSize / 1024 / 1024).toFixed(2)} MB) exceeds limit of ${config.maxDocumentSizeMB} MB`);
    }

    return { valid: errors.length === 0, errors };
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    processSelectedFiles(files);
  };

  const processSelectedFiles = (files) => {
    const validation = validateFiles(files);
    
    if (validation.valid) {
      setSelectedFiles(files);
      setValidationErrors([]);
    } else {
      setValidationErrors(validation.errors);
      setSelectedFiles([]);
    }
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    processSelectedFiles(files);
  };

  const handleProcessDocument = async (documentId) => {
    try {
      setProcessStatus({ message: 'Starting processing…', isError: false });

      // Immediately update local state to show progress bar
      setDocuments(prevDocs => prevDocs.map(doc => {
        if (doc.document_id === documentId) {
          return {
            ...doc,
            status: 'processing',
            // Don't set processing_progress here -- let the backend provide
            // the real chunk count. The UI will show "Starting...Calculating..."
            // until the first poll returns actual progress data.
            processing_progress: null,
          };
        }
        return doc;
      }));

      const resolved = resolveParamsForApi(globalParams);
      const response = await apiService.processDocument(graphId, documentId, resolved.provider_id);

      if (response.success) {
        setProcessStatus({
          message: response.message || 'Document submitted for processing.',
          isError: false,
        });
        // Reload to get accurate progress data from backend
        loadDocuments();
        setTimeout(() => loadDocuments(), 2000);
      } else {
        // Revert optimistic update on failure
        loadDocuments();
        setProcessStatus({
          message: response.error || response.message || 'Failed to start processing.',
          isError: true,
        });
      }
    } catch (err) {
      console.error('Process document error:', err);
      loadDocuments(); // Revert on error
      const message =
        err.response?.data?.error ||
        err.message ||
        'Failed to start processing.';
      setProcessStatus({ message, isError: true });
    }
  };

  // Re-persist a graph from saved processing_output (when build succeeded but DB persist failed)
  const handleRepersist = async (documentId, filename) => {
    if (!window.confirm(
      `Re-persist graph for "${filename}"?\n\n` +
      `This will save the previously built graph to the database and upload vectors to Pinecone. ` +
      `This may take several minutes for large graphs.`
    )) {
      return;
    }

    try {
      setRepersistingDocId(documentId);
      setProcessStatus({ message: 'Re-persisting graph (this may take a few minutes)...', isError: false });
      const result = await apiService.repersistGraph(documentId);
      
      if (result.persisted) {
        setProcessStatus({
          message: `Graph re-persisted: ${result.node_count || 0} nodes, ${result.synapse_count || 0} synapses`,
          isError: false
        });
        // Update local state to remove persist_failed flag
        setDocuments(prevDocs => prevDocs.map(doc =>
          doc.document_id === documentId
            ? { ...doc, persist_failed: false }
            : doc
        ));
        setTimeout(() => loadDocuments(), 1000);
      } else {
        setProcessStatus({
          message: `Re-persist failed: ${result.error || 'Unknown error'}`,
          isError: true
        });
      }
    } catch (err) {
      console.error('Repersist error:', err);
      setProcessStatus({
        message: err.response?.data?.detail || err.message || 'Failed to re-persist graph',
        isError: true
      });
    } finally {
      setRepersistingDocId(null);
    }
  };

  // Cancel document processing
  const handleCancelDocument = async (documentId, filename) => {
    if (!window.confirm(`Cancel processing for "${filename}"?\n\nThis will stop the document processing. The document can be re-processed later.`)) {
      return;
    }
    
    try {
      setProcessStatus({ message: 'Canceling...', isError: false });
      const response = await apiService.cancelDocument(documentId);
      
      if (response.success) {
        setProcessStatus({
          message: `Processing canceled for "${filename}"`,
          isError: false
        });
        // Update local state immediately
        setDocuments(prevDocs => prevDocs.map(doc => 
          doc.document_id === documentId 
            ? { ...doc, status: 'canceled' }
            : doc
        ));
        // Also reload to get fresh data
        setTimeout(() => loadDocuments(), 1000);
      } else {
        setProcessStatus({
          message: response.error || 'Failed to cancel processing',
          isError: true
        });
      }
    } catch (err) {
      console.error('Cancel document error:', err);
      setProcessStatus({
        message: err.response?.data?.error || 'Failed to cancel processing',
        isError: true
      });
    }
  };

  // Delete document (only for unprocessed documents)
  const handleDeleteDocument = async (documentId, filename) => {
    if (!window.confirm(`Delete "${filename}"?\n\nThis will permanently remove the document. This action cannot be undone.`)) {
      return;
    }
    
    try {
      setProcessStatus({ message: 'Deleting...', isError: false });
      const response = await apiService.deleteDocument(documentId);
      
      if (response.success) {
        setProcessStatus({
          message: response.message || `Document "${filename}" deleted`,
          isError: false
        });
        // Remove from local state immediately
        setDocuments(prevDocs => prevDocs.filter(doc => doc.document_id !== documentId));
        // Also remove from consolidation selection if selected
        setSelectedDocsForConsolidation(prev => {
          const newSet = new Set(prev);
          newSet.delete(documentId);
          return newSet;
        });
      } else {
        setProcessStatus({
          message: response.error || 'Failed to delete document',
          isError: true
        });
      }
    } catch (err) {
      console.error('Delete document error:', err);
      setProcessStatus({
        message: err.response?.data?.error || 'Failed to delete document',
        isError: true
      });
    }
  };

  // Chunk a document
  const handleChunkDocument = async (documentId) => {
    try {
      setChunkingDocId(documentId);
      setProcessStatus({ message: 'Chunking document...', isError: false });
      
      const response = await apiService.chunkDocument(graphId, documentId, resolveParamsForApi(globalParams));
      
      if (response.success) {
        setProcessStatus({
          message: `Document chunked: ${response.chunks_created} chunks created`,
          isError: false
        });
        // Reload documents and chunks
        loadDocuments();
        loadChunkCounts();
      } else {
        setProcessStatus({
          message: response.error || 'Failed to chunk document',
          isError: true
        });
        // Still refresh documents so UI stays in sync with backend state
        loadDocuments();
        loadChunkCounts();
      }
    } catch (err) {
      console.error('Chunk document error:', err);
      const isTimeout = err.code === 'ECONNABORTED' || err.message?.includes('timeout');
      if (isTimeout) {
        // The backend may still be processing. Poll to check if it completed.
        setProcessStatus({
          message: 'Chunking is taking longer than expected — checking status...',
          isError: false
        });
        // Poll a few times to see if the backend completed
        let resolved = false;
        for (let attempt = 0; attempt < 6; attempt++) {
          await new Promise(r => setTimeout(r, 5000)); // wait 5s between polls
          try {
            const docs = await apiService.getGraphDocuments(graphId);
            if (docs.success) {
              const doc = (docs.documents || []).find(d => d.document_id === documentId);
              if (doc && doc.chunking_status === 'chunked') {
                setProcessStatus({
                  message: 'Chunking completed successfully!',
                  isError: false
                });
                setDocuments(docs.documents);
                loadChunkCounts();
                resolved = true;
                break;
              } else if (doc && doc.chunking_status === 'chunk_failed') {
                setProcessStatus({
                  message: 'Chunking failed on the server. Please retry.',
                  isError: true
                });
                setDocuments(docs.documents);
                resolved = true;
                break;
              }
              // Still 'chunking' — keep polling
              setProcessStatus({
                message: `Still chunking... (checked ${attempt + 1}/6)`,
                isError: false
              });
            }
          } catch (_) {
            // poll failed, will try again
          }
        }
        if (!resolved) {
          setProcessStatus({
            message: 'Chunking is still in progress on the server. Refresh the page in a moment.',
            isError: false
          });
          loadDocuments();
          loadChunkCounts();
        }
      } else {
        setProcessStatus({
          message: err.response?.data?.error || 'Failed to chunk document. Please try again.',
          isError: true
        });
        // Refresh documents to stay in sync with backend
        loadDocuments();
        loadChunkCounts();
      }
    } finally {
      setChunkingDocId(null);
    }
  };

  // Generate per-document graph prompt via AI
  const handleGenerateDocumentPrompt = async (documentId) => {
    try {
      setGeneratingPromptDocId(documentId);
      setProcessStatus({ message: 'Generating custom graph prompt for document...', isError: false });
      
      const resolved = resolveParamsForApi(globalParams);
      const response = await apiService.generateDocumentPrompt(documentId, globalParams.build_approach, resolved.provider_id);
      
      if (response.success) {
        const isCompiler = (globalParams.build_approach || '').startsWith('compiler_');
        setProcessStatus({
          message: isCompiler
            ? 'Custom compiler Phase 1 and Phase 3 prompts generated and saved for this document.'
            : 'Custom graph extraction prompt generated and saved for this document.',
          isError: false
        });
        // Refresh prompts status
        loadPromptsStatus();
        // Reload documents to reflect custom_prompts flag
        loadDocuments();
      } else {
        setProcessStatus({
          message: response.error || 'Failed to generate document prompt',
          isError: true
        });
      }
    } catch (err) {
      console.error('Generate document prompt error:', err);
      setProcessStatus({
        message: err.response?.data?.error || err.response?.data?.message || 'Failed to generate document prompt',
        isError: true
      });
    } finally {
      setGeneratingPromptDocId(null);
    }
  };

  // Handle chunk modal open
  const handleOpenChunkModal = () => {
    setShowChunkModal(true);
  };

  // Handle chunks updated (from modal)
  const handleChunksUpdated = () => {
    loadChunkCounts();
  };

  // Apply global params to all documents
  const handleApplyToAll = async () => {
    try {
      setProcessStatus({ message: 'Applying settings to all documents...', isError: false });
      const response = await apiService.updateGraphDocumentsParams(graphId, resolveParamsForApi(globalParams));
      if (response.success) {
        setProcessStatus({
          message: `Settings applied to ${response.updated_count} document(s)`,
          isError: false
        });
        loadDocuments();
      } else {
        setProcessStatus({
          message: response.error || 'Failed to apply settings',
          isError: true
        });
      }
    } catch (err) {
      console.error('Apply settings error:', err);
      setProcessStatus({
        message: err.response?.data?.error || 'Failed to apply settings',
        isError: true
      });
    }
  };

  // Process all unprocessed documents
  const handleProcessAll = async () => {
    try {
      setProcessingAll(true);
      setProcessStatus({ message: 'Queuing all documents for processing...', isError: false });
      
      const response = await apiService.processAllDocuments(graphId);
      
      if (response.success) {
        setProcessStatus({
          message: response.message || `${response.queued_count} document(s) queued for processing`,
          isError: false
        });
        loadDocuments();
      } else {
        setProcessStatus({
          message: response.error || 'Failed to process documents',
          isError: true
        });
      }
    } catch (err) {
      console.error('Process all error:', err);
      setProcessStatus({
        message: err.response?.data?.error || 'Failed to process documents',
        isError: true
      });
    } finally {
      setProcessingAll(false);
    }
  };

  // Process only pending (unprocessed) chunks
  const handleProcessPendingChunks = async () => {
    try {
      setProcessingPendingChunks(true);
      setProcessStatus({ message: 'Queuing pending chunks for processing...', isError: false });
      
      const response = await apiService.processPendingChunks(graphId);
      
      if (response.success) {
        const pendingCount = response.pending_count || 0;
        
        // Set optimistic local progress with correct pending count
        if (response.document_id && pendingCount > 0) {
          setDocuments(prevDocs => {
            return prevDocs.map(doc => {
              if (doc.document_id === response.document_id) {
                return {
                  ...doc,
                  status: 'processing',
                  processing_progress: {
                    started_at: new Date().toISOString(),
                    total_chunks: pendingCount,
                    estimated_seconds: pendingCount * 45,
                    pending_only: true,
                  }
                };
              }
              return doc;
            });
          });
        }
        
        setProcessStatus({
          message: response.message || `${pendingCount} pending chunk(s) queued`,
          isError: false
        });
        loadChunkCounts();
      } else {
        setProcessStatus({
          message: response.error || 'Failed to process pending chunks',
          isError: true
        });
      }
    } catch (err) {
      console.error('Process pending chunks error:', err);
      setProcessStatus({
        message: err.response?.data?.error || 'Failed to process pending chunks',
        isError: true
      });
    } finally {
      setProcessingPendingChunks(false);
    }
  };

  // Update params for a single document
  const handleUpdateDocParams = async (documentId, params) => {
    try {
      const response = await apiService.updateDocumentParams(documentId, resolveParamsForApi(params));
      if (response.success) {
        loadDocuments();
      } else {
        setProcessStatus({
          message: response.error || 'Failed to update parameters',
          isError: true
        });
      }
    } catch (err) {
      console.error('Update params error:', err);
      setProcessStatus({
        message: err.response?.data?.error || 'Failed to update parameters',
        isError: true
      });
    }
  };

  // Show output modal
  const handleShowOutput = async (documentId) => {
    try {
      setLoadingOutput(true);
      setShowOutputModal(true);
      const response = await apiService.getDocumentOutput(documentId);
      if (response.success) {
        setSelectedOutput(response.output);
      } else {
        setSelectedOutput({ error: response.error || 'No output available' });
      }
    } catch (err) {
      console.error('Get output error:', err);
      setSelectedOutput({ error: err.response?.data?.error || 'Failed to load output' });
    } finally {
      setLoadingOutput(false);
    }
  };

  const closeOutputModal = () => {
    setShowOutputModal(false);
    setSelectedOutput(null);
  };


  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    try {
      setUploading(true);
      setUploadProgress(0);
      setError(null);
      setSuccess(null);

      const response = await apiService.uploadDocuments(
        graphId,
        selectedFiles,
        (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
        resolveParamsForApi(globalParams)
      );

      if (response.success) {
        setSuccess(response.message);
        setSelectedFiles([]);
        setValidationErrors([]);
        await loadDocuments();
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setError(response.error || 'Upload failed');
        if (response.errors && response.errors.length > 0) {
          setValidationErrors(response.errors.map(e => `${e.filename}: ${e.error}`));
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || 'Failed to upload documents');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'uploaded': 'status-uploaded',
      'queued': 'status-queued',
      'processing': 'status-processing',
      'processed': 'status-processed',
      'failed': 'status-failed',
      'canceled': 'status-canceled'
    };
    return statusMap[status] || 'status-unknown';
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      'uploaded': '📄',
      'queued': '⏳',
      'processing': '⚙️',
      'processed': '✅',
      'failed': '❌',
      'canceled': '🚫'
    };
    return iconMap[status] || '❓';
  };

  if (loading) {
    return (
      <div className="build-graph-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading graph...</p>
        </div>
      </div>
    );
  }

  if (error && !graph) {
    return (
      <div className="build-graph-page">
        <div className="page-header">
          <h1 className="page-title">Build Graph</h1>
        </div>
        <div className="alert alert-error">{error}</div>
        <button className="btn-primary" onClick={handleBack}>
          Back to Graphs
        </button>
      </div>
    );
  }

  return (
    <div className="build-graph-page">
      <BuildGraphMenu 
        promptsStatus={promptsStatus}
        onOpenPrompts={() => setShowPromptsModal(true)}
        workspaceId={graph?.workspaceId}
        defaultProviderId={
          globalParams.model?.startsWith('provider::')
            ? globalParams.model.split('::')[1]
            : undefined
        }
      />
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Build Graph</h1>
          {graph && <p className="page-subtitle">{graph.graphName}</p>}
        </div>
        <div className="header-right">
          <button className="btn-secondary" onClick={handleBack}>
            Back to Graphs
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Graph Info */}
      {graph && (
        <div className="graph-info-card">
          <h3>Graph Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Workspace:</span>
              <span className="info-value">{graph.workspaceName}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Type:</span>
              <span className="info-value">{graph.graphType}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Status:</span>
              <span className="info-value">{graph.status}</span>
            </div>
            {graph.description && (
              <div className="info-item full-width">
                <span className="info-label">Description:</span>
                <span className="info-value">{graph.description}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Router Configuration Panel - shown only for Router graphs */}
      {graph && graph.graphType === 'Router' && (
        <RouterConfigPanel graphId={graphId} processingParams={globalParams} onChunksGenerated={() => {
          loadDocuments();
        }} />
      )}

      {/* Upload Configuration Info - hidden for Router graphs */}
      {(!graph || graph.graphType !== 'Router') && (
      <div className="config-info">
        <p>
          <strong>Upload Limits:</strong> Max {config.maxDocumentsPerUpload} files, 
          Total size limit: {config.maxDocumentSizeMB} MB, 
          Allowed types: {config.allowedDocumentTypes.join(', ').toUpperCase()}
        </p>
      </div>
      )}

      {/* File Upload Area - hidden for Router graphs */}
      {(!graph || graph.graphType !== 'Router') && (
      <div className="upload-section">
        <h2>Upload Documents</h2>
        <p className="section-description">
          Upload documents containing domain knowledge, rules, processes, or ontologies in natural language.
        </p>

        <div
          className={`drop-zone ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={config.allowedDocumentTypes.map(t => `.${t}`).join(',')}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <div className="drop-zone-content">
            <div className="upload-icon">📁</div>
            <p className="drop-zone-text">
              <strong>Click to upload</strong> or drag and drop
            </p>
            <p className="drop-zone-hint">
              PDF, DOCX, TXT, or DOC (max {config.maxDocumentSizeMB}MB total)
            </p>
            <p className="drop-zone-settings">
              Will use: <strong>{globalParams.chunking_strategy === 'structure_aware' ? '📋 Structure-Aware' : '📄 Word-Based'}</strong> chunking
            </p>
          </div>
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="validation-errors">
            {validationErrors.map((err, idx) => (
              <div key={idx} className="validation-error">{err}</div>
            ))}
          </div>
        )}

        {/* Selected Files */}
        {selectedFiles.length > 0 && (
          <div className="selected-files">
            <h3>Selected Files ({selectedFiles.length})</h3>
            <div className="file-list">
              {selectedFiles.map((file, idx) => (
                <div key={idx} className="file-item">
                  <span className="file-icon">📄</span>
                  <span className="file-name">{file.name}</span>
                  <span className="file-size">{formatFileSize(file.size)}</span>
                </div>
              ))}
            </div>
            <button
              className="btn-primary"
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? `Uploading... ${uploadProgress}%` : 'Upload Files'}
            </button>
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
            </div>
            <p className="progress-text">{uploadProgress}% Complete</p>
          </div>
        )}
      </div>
      )}

      {/* Processing Settings Panel */}
      <ProcessingSettingsPanel
        globalParams={globalParams}
        setGlobalParams={setGlobalParams}
        modelOptions={modelOptions}
        onApplyToAll={handleApplyToAll}
        onProcessAll={handleProcessAll}
        showActions={true}
        processingAll={processingAll}
        hasUnprocessedDocs={documents.filter(d => ['uploaded', 'queued'].includes(d.status)).length > 0}
        collapsible={true}
        defaultOpen={showSettings}
      />

      {/* Graph Consolidation Panel */}
      <div className="consolidation-section">
        <div className="consolidation-header">
          <h2>Graph Consolidation</h2>
          <div className="consolidation-actions">
            {/* Manual Consolidate Graph button.
                Hidden for FGE: the precision pipeline already runs cross-document
                consolidation automatically after every document build (see
                _run_cross_doc_consolidation in fast_graph_routes.py); a manual run
                would either be redundant or skipped by the ConsolidatedDocumentPairs
                short-circuit. The "Latest Consolidation Results" card below still
                shows the most recent auto-run. */}
            {!isFge && (
              <button 
                className="btn-primary consolidate-btn"
                onClick={handleConsolidateGraph}
                disabled={consolidating || selectedProcessedCount === 0}
                title={
                  consolidating 
                    ? 'Consolidation in progress...' 
                    : selectedProcessedCount === 0
                      ? 'Select at least one processed document to consolidate' 
                      : `Consolidate ${selectedProcessedCount} selected document(s)`
                }
              >
                {consolidating ? (
                  <>
                    <span className="btn-spinner"></span>
                    Consolidating...
                  </>
                ) : `Consolidate Graph (${selectedProcessedCount})`}
              </button>
            )}
            {reviewQueueCount > 0 && (
              <button 
                className="btn-secondary review-queue-btn"
                onClick={() => { setShowReviewModal(true); loadReviewItems(); }}
              >
                Review Queue ({reviewQueueCount})
              </button>
            )}
            <button 
              className="btn-warning reprocess-btn"
              onClick={() => setShowReprocessModal(true)}
              disabled={consolidating || reprocessing}
              title="Delete all graph data (nodes, connections, Q/A) and rebuild from existing chunks"
            >
              {reprocessing ? (
                <>
                  <span className="btn-spinner"></span>
                  Reprocessing...
                </>
              ) : 'Reprocess Graph'}
            </button>
            {documents.some(d => d.status === 'failed' || d.status === 'canceled') && (
              <button
                className="btn-info resume-btn"
                onClick={handleResumeProcessing}
                disabled={consolidating || resuming || reprocessing}
                title="Resume processing from the last successfully processed chunk"
              >
                {resuming ? (
                  <>
                    <span className="btn-spinner"></span>
                    Resuming...
                  </>
                ) : 'Resume Processing'}
              </button>
            )}
          </div>
        </div>

        {/* FGE auto-consolidation note: the precision pipeline runs cross-document
            consolidation automatically after each document, so the manual button is
            hidden. The "Latest Consolidation Results" card and Review Queue below
            still surface what was applied and what needs review. */}
        {isFge && (
          <p className="consolidation-hint">
            Documents are consolidated automatically after each upload. Latest results
            and items needing review appear below.
          </p>
        )}
        
        {/* Consolidation In Progress Status Bar */}
        {consolidating && (
          <div className="consolidation-progress-panel">
            <div className="progress-header">
              <span className="progress-icon">⚙️</span>
              <span className="progress-title">Consolidation In Progress</span>
            </div>
            <div className="progress-bar-container">
              <div className="progress-bar-animated"></div>
            </div>
            <div className="progress-message">
              {consolidationStatus?.message || 'Analyzing graph and validating candidates...'}
            </div>
            <div className="progress-details">
              <span className="detail-item">
                <strong>ID:</strong> {consolidationId?.substring(0, 8)}...
              </span>
              <span className="detail-item">
                <strong>Status:</strong> {consolidationStatus?.status || 'running'}
              </span>
            </div>
          </div>
        )}
        
        {/* Consolidation Results - shown when complete */}
        {!consolidating && consolidationHistory.length > 0 && (
          <div className="consolidation-results">
            <div className="results-header">
              <h4>Latest Consolidation Results</h4>
              <button 
                className="btn-link view-all-link"
                onClick={() => setShowConsolidationDetails(true)}
              >
                View All History ({consolidationHistory.length})
              </button>
            </div>
            {consolidationHistory.slice(0, 1).map((record) => {
              // Calculate duration if completed
              let duration = null;
              if (record.started_at && record.completed_at) {
                const start = new Date(record.started_at);
                const end = new Date(record.completed_at);
                const durationMs = end - start;
                const mins = Math.floor(durationMs / 60000);
                const secs = Math.floor((durationMs % 60000) / 1000);
                duration = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
              }
              
              return (
              <div key={record.consolidation_id} className="result-card">
                <div className="result-header">
                  <span className={`status-badge status-${record.status}`}>
                    {record.status === 'completed' ? '✓ Completed' : 
                     record.status === 'interrupted' ? '⚠ Interrupted' : record.status}
                  </span>
                  <div className="result-meta">
                    <span className="result-date">
                      📅 {new Date(record.started_at).toLocaleString()}
                    </span>
                    {duration && (
                      <span className="result-duration">
                        ⏱ Duration: {duration}
                      </span>
                    )}
                    <span className="result-docs">
                      📄 {documents.length} documents in graph
                    </span>
                  </div>
                </div>
                {record.summary && (
                  <div className="result-summary">
                    {/* Show message if no candidates found */}
                    {record.summary.message && (
                      <div className="summary-message-inline">
                        <span>ℹ️ {record.summary.message}</span>
                        {record.summary.incremental_mode && (
                          <span className="mode-badge">Incremental Mode</span>
                        )}
                      </div>
                    )}
                    
                    {/* Show stats if candidates were analyzed */}
                    {record.summary.total_candidates !== undefined && record.summary.total_candidates > 0 && (
                      <div className="summary-grid">
                        {record.summary.auto_applied && (
                          <div className="summary-item auto-applied">
                            <span className="summary-label">Auto-Applied (High Confidence)</span>
                            <span className="summary-values">
                              {(record.summary.auto_applied.merges || 0)} merges, 
                              {' '}{(record.summary.auto_applied.relationships || 0)} relationships, 
                              {' '}{(record.summary.auto_applied.gates || 0)} gates
                            </span>
                          </div>
                        )}
                        {record.summary.applied_with_warning && (
                          <div className="summary-item with-warning">
                            <span className="summary-label">Applied with Warning (Medium)</span>
                            <span className="summary-values">
                              {(record.summary.applied_with_warning.merges || 0)} merges, 
                              {' '}{(record.summary.applied_with_warning.relationships || 0)} relationships
                            </span>
                          </div>
                        )}
                        {record.summary.queued_for_review && (
                          <div className="summary-item for-review">
                            <span className="summary-label">Queued for Review (Low Confidence)</span>
                            <span className="summary-values">
                              {(record.summary.queued_for_review.merges || 0)} merges, 
                              {' '}{(record.summary.queued_for_review.relationships || 0)} relationships
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div className="result-actions">
                  {reviewQueueCount > 0 && (
                    <button 
                      className="btn-action review-action"
                      onClick={() => { setShowReviewModal(true); loadReviewItems(); }}
                    >
                      🔍 Review {reviewQueueCount} Pending Items
                    </button>
                  )}
                  <button 
                    className="btn-action details-action"
                    onClick={() => setShowConsolidationDetails(true)}
                  >
                    📊 View Full Details
                  </button>
                </div>
              </div>
            );
            })}
          </div>
        )}
        
        {/* Hint when not enough documents */}
        {documents.length < 2 && !consolidating && (
          <p className="consolidation-hint">
            📚 Upload at least 2 documents to enable cross-document consolidation.
          </p>
        )}
        
        {/* Ready to consolidate message.
            Hidden for FGE: the manual Consolidate button is hidden, and the
            precision pipeline auto-consolidates after each document, so this
            prompt would be misleading. */}
        {!isFge && documents.length >= 2 && !consolidating && consolidationHistory.length === 0 && (
          <p className="consolidation-ready">
            ✨ Ready to consolidate! Click the button above to analyze and link information across your {documents.length} documents.
          </p>
        )}
      </div>

      {/* Uploaded Documents */}
      <div className="documents-section">
        <div className="documents-section__header">
          <h2>Uploaded Documents ({documents.length})</h2>
          <div className="documents-section__actions">
            {/* Chunk Management buttons.
                Hidden for FGE: GraphBuilder re-chunks the source file internally and
                does not consume DB chunk rows, so these graph-level controls are
                misleading in FGE mode. */}
            {!isFge && chunkCounts && chunkCounts.total_count > 0 && (
              <button className="btn-link view-all-chunks" onClick={handleOpenChunkModal}>
                View All Chunks ({chunkCounts.total_count})
              </button>
            )}
            {!isFge && chunkCounts && chunkCounts.pending_count > 0 && (
              <button
                className="btn-primary btn-process-pending"
                onClick={handleProcessPendingChunks}
                disabled={processingPendingChunks}
                title={`Process ${chunkCounts.pending_count} pending chunk(s) through the graph builder pipeline`}
              >
                {processingPendingChunks ? 'Processing...' : `Process Pending Chunks (${chunkCounts.pending_count})`}
              </button>
            )}
            {!isFge && (
              <button 
                className="btn-secondary btn-add-chunk" 
                onClick={handleOpenChunkModal}
                title="Add manual chunks to the graph"
              >
                + Add Chunk
              </button>
            )}
            <button className="btn-secondary btn-refresh" onClick={() => { loadDocuments(); loadChunkCounts(); }}>
              Refresh
            </button>
          </div>
        </div>

        {documents.length === 0 ? (
          <div className="empty-state">
            <p>No documents uploaded yet. Upload your first document to get started.</p>
          </div>
        ) : (
          <div className="documents-table-container">
            {/* Consolidation Selection Controls.
                Hidden for FGE: the manual Consolidate button is hidden in FGE mode,
                so per-document inclusion selection is meaningless. */}
            {!isFge && (
              <div className="consolidation-selection-bar">
                <span className="selection-label">
                  📋 Include in Consolidation: <strong>{selectedProcessedCount}</strong> of {documents.filter(d => d.status === 'processed').length} processed documents selected
                </span>
                <div className="selection-actions">
                  <button 
                    className="btn-sm btn-link"
                    onClick={selectAllProcessedDocs}
                  >
                    Select All Processed
                  </button>
                  <button 
                    className="btn-sm btn-link"
                    onClick={deselectAllDocs}
                  >
                    Deselect All
                  </button>
                </div>
              </div>
            )}
            <table className="documents-table">
              <thead>
                <tr>
                  {/* Consolidation-inclusion checkbox column.
                      Hidden for FGE (manual Consolidate button is hidden too). */}
                  {!isFge && (
                    <th className="col-checkbox">
                      <span title="Include in consolidation">🔗</span>
                    </th>
                  )}
                  <th>Status</th>
                  <th>Filename</th>
                  <th>Size</th>
                  <th>Type</th>
                  <th>Uploaded</th>
                  <th>Job Status</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => {
                  const processJob = doc.process_job || {};
                  const docParams = doc.processing_params || DEFAULT_PARAMS;
                  const isExpanded = expandedDocId === doc.document_id;
                  const canProcess = ['uploaded', 'queued'].includes(doc.status);
                  const canEdit = canProcess;
                  const isProcessed = doc.status === 'processed';
                  const isSelectedForConsolidation = selectedDocsForConsolidation.has(doc.document_id);
                  
                  // Calculate progress for processing documents
                  const progress = doc.processing_progress;
                  let progressPercent = 0;
                  let progressText = '';
                  let remainingTime = '';
                  
                  if (doc.status === 'processing' && progress) {
                    // Parse started_at - handle both UTC (with +00:00 or Z) and local
                    let startedAtStr = progress.started_at;
                    // Ensure UTC parsing if no timezone indicator
                    if (startedAtStr && !startedAtStr.includes('+') && !startedAtStr.includes('Z')) {
                      startedAtStr += 'Z';
                    }
                    const startedAt = new Date(startedAtStr);
                    const elapsedSeconds = Math.max(0, (Date.now() - startedAt.getTime()) / 1000);
                    const estimatedSeconds = progress.estimated_seconds || 120; // default 2 min
                    const totalChunks = progress.total_chunks || 1;
                    
                    // Calculate progress (cap at 95% until actually done)
                    progressPercent = Math.min(95, Math.max(5, Math.round((elapsedSeconds / estimatedSeconds) * 100)));
                    progressText = `${totalChunks} chunk${totalChunks > 1 ? 's' : ''}`;
                    
                    const remaining = Math.max(0, estimatedSeconds - elapsedSeconds);
                    if (remaining > 60) {
                      remainingTime = `~${Math.ceil(remaining / 60)} min remaining`;
                    } else if (remaining > 0) {
                      remainingTime = `~${Math.round(remaining)}s remaining`;
                    } else {
                      // Show specific finishing phase if available
                      const finishingPhase = progress.finishing_phase;
                      remainingTime = finishingPhase || 'Finishing up...';
                    }
                  } else if (doc.status === 'processing') {
                    progressPercent = 5; // Just started
                    progressText = 'Starting...';
                    remainingTime = 'Calculating...';
                  }
                  
                  return (
                    <React.Fragment key={doc.document_id}>
                      {/* Document Action Toolbar Row - horizontal buttons above the document */}
                      <tr className="document-toolbar-row">
                        <td colSpan={isFge ? 7 : 8}>
                          <div className="document-toolbar">
                            <span className="document-toolbar__name">{doc.filename}</span>
                            <div className="document-toolbar__actions">
                              {/* Chunk Document button.
                                  Hidden for FGE: GraphBuilder re-chunks the source file
                                  internally and does not consume DB chunk rows. */}
                              {!isFge && canProcess && doc.chunking_status !== 'chunked' && (
                                <button 
                                  className={`btn-sm ${doc.chunking_status === 'chunk_failed' ? 'btn-danger' : 'btn-secondary'}`}
                                  onClick={() => handleChunkDocument(doc.document_id)}
                                  disabled={chunkingDocId === doc.document_id}
                                >
                                  {chunkingDocId === doc.document_id
                                    ? 'Chunking...'
                                    : doc.chunking_status === 'chunk_failed'
                                      ? 'Retry Chunk'
                                      : doc.chunking_status === 'chunking'
                                        ? 'Chunking...'
                                        : 'Chunk'}
                                </button>
                              )}
                              {/* View/Edit chunks link (DB chunks; not used by FGE) */}
                              {!isFge && doc.chunking_status === 'chunked' && (
                                <button 
                                  className="btn-secondary btn-sm" 
                                  onClick={() => handleOpenChunkModal()}
                                >
                                  View Chunks
                                </button>
                              )}
                              {/* Create Graph Prompt OR View/Edit Prompt — mutually exclusive.
                                  Hidden for Fast Graph Engine: the rule-based pipeline
                                  doesn't use LLM extraction prompts, so the button would be misleading. */}
                              {globalParams.build_approach !== 'fast_graph_engine_precision'
                                && doc.chunking_status === 'chunked'
                                && !['processing'].includes(doc.status) && (
                                doc.has_custom_prompts ? (
                                  <button
                                    className="btn-secondary btn-sm"
                                    onClick={() => {
                                      setPromptModalDocId(doc.document_id);
                                      setPromptModalDocName(doc.filename);
                                    }}
                                    title="View or edit the custom graph extraction prompt for this document"
                                  >
                                    View/Edit Prompt
                                  </button>
                                ) : (
                                  <button 
                                    className="btn-accent btn-sm"
                                    onClick={() => handleGenerateDocumentPrompt(doc.document_id)}
                                    disabled={generatingPromptDocId === doc.document_id}
                                    title="Generate a custom graph extraction prompt for this document using AI"
                                  >
                                    {generatingPromptDocId === doc.document_id ? 'Generating...' : 'Create Graph Prompt'}
                                  </button>
                                )
                              )}
                              {/* Process */}
                              {canProcess && (
                                <button 
                                  className="btn-primary btn-sm" 
                                  onClick={() => handleProcessDocument(doc.document_id)}
                                >
                                  Process
                                </button>
                              )}
                              {/* Cancel */}
                              {(doc.status === 'processing' || doc.status === 'queued') && (
                                <button 
                                  className="btn-danger btn-sm" 
                                  onClick={() => handleCancelDocument(doc.document_id, doc.filename)}
                                >
                                  Cancel
                                </button>
                              )}
                              {/* Retry Persist — shown when build succeeded but DB persist failed */}
                              {doc.status === 'processed' && doc.persist_failed && (
                                <button 
                                  className="btn-primary btn-sm" 
                                  onClick={() => handleRepersist(doc.document_id, doc.filename)}
                                  disabled={repersistingDocId === doc.document_id}
                                  title="Graph was built but not saved to database. Click to retry."
                                >
                                  {repersistingDocId === doc.document_id ? 'Persisting...' : 'Retry Persist'}
                                </button>
                              )}
                              {/* Delete */}
                              {['uploaded', 'queued', 'failed', 'cancelled', 'canceled'].includes(doc.status) && (
                                <button 
                                  className="btn-danger btn-sm" 
                                  onClick={() => handleDeleteDocument(doc.document_id, doc.filename)}
                                  title="Delete this document"
                                >
                                  Delete
                                </button>
                              )}
                              {/* Download original file */}
                              <button
                                className="btn-secondary btn-sm"
                                onClick={() => apiService.downloadDocumentFile(doc.document_id, doc.filename)}
                                title="Download original file"
                              >
                                Download
                              </button>
                              {/* See Output */}
                              {doc.has_output && (
                                <button 
                                  className="btn-secondary btn-sm" 
                                  onClick={() => handleShowOutput(doc.document_id)}
                                >
                                  See Output
                                </button>
                              )}
                              {/* Settings toggle.
                                  Hidden for FGE: the expanded row exposes composite-only
                                  knobs (Model, Max Iterations, Chunk Retries, Coverage,
                                  Min Coverage %, Re-extract) that the precision pipeline
                                  does not consume. */}
                              {!isFge && (
                                <button 
                                  className="btn-link settings-toggle"
                                  onClick={() => setExpandedDocId(isExpanded ? null : doc.document_id)}
                                >
                                  {isExpanded ? '▲ Settings' : '▼ Settings'}
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr className={isSelectedForConsolidation ? 'selected-for-consolidation' : ''}>
                        {/* Consolidation-inclusion checkbox cell.
                            Hidden for FGE (column is also hidden in the header). */}
                        {!isFge && (
                          <td className="checkbox-cell">
                            <input
                              type="checkbox"
                              className="consolidation-checkbox"
                              checked={isSelectedForConsolidation}
                              onChange={() => toggleDocForConsolidation(doc.document_id)}
                              disabled={!isProcessed}
                              title={isProcessed ? 'Include in consolidation' : 'Only processed documents can be consolidated'}
                            />
                          </td>
                        )}
                        <td className="status-cell">
                          <span className={`status-badge ${getStatusBadgeClass(doc.status)}`}>
                            {getStatusIcon(doc.status)} {doc.status}
                          </span>
                          {!isFge && doc.chunking_status === 'chunk_failed' && doc.status !== 'processing' && (
                            <div className="chunk-status-warning">
                              Chunking failed — click "Retry Chunk" above
                            </div>
                          )}
                          {!isFge && doc.chunking_status === 'chunking' && doc.status !== 'processing' && (
                            <div className="chunk-status-info">
                              Chunking in progress...
                            </div>
                          )}
                          {doc.status === 'processing' && (
                            <div className="progress-container">
                              <div className="progress-bar">
                                <div 
                                  className="progress-fill" 
                                  style={{ width: `${progressPercent}%` }}
                                />
                              </div>
                              <div className="progress-info">
                                <span className="progress-text">{progressText}</span>
                                <span className="progress-time">{remainingTime}</span>
                              </div>
                              {/* Chunk Progress Detail Panel */}
                              {progress && progress.total_chunks > 1 && (
                                <div className="chunk-progress-panel">
                                  <div className="chunk-progress-panel__header">
                                    <span className="chunk-progress-panel__title">Chunk Progress</span>
                                    <span className="chunk-progress-panel__summary">
                                      {Math.min(Math.floor(progressPercent / 100 * progress.total_chunks) + 1, progress.total_chunks)} of {progress.total_chunks} chunks
                                    </span>
                                  </div>
                                  <div className="chunk-list">
                                    {Array.from({ length: progress.total_chunks }, (_, i) => {
                                      const estimatedChunkIndex = Math.floor(progressPercent / 100 * progress.total_chunks);
                                      const isCompleted = i < estimatedChunkIndex;
                                      const isProcessing = i === estimatedChunkIndex;
                                      const isPending = i > estimatedChunkIndex;
                                      
                                      return (
                                        <div 
                                          key={i} 
                                          className={`chunk-item ${isCompleted ? 'chunk-item--completed' : ''} ${isProcessing ? 'chunk-item--processing' : ''} ${isPending ? 'chunk-item--pending' : ''}`}
                                        >
                                          <span className="chunk-item__icon">
                                            {isCompleted ? '✓' : isProcessing ? '⏳' : '○'}
                                          </span>
                                          <span className="chunk-item__name">Chunk {i + 1}</span>
                                          <span className="chunk-item__status">
                                            {isCompleted ? 'completed' : isProcessing ? 'processing...' : 'pending'}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="filename-cell">{doc.filename}</td>
                        <td>{formatFileSize(doc.size_bytes)}</td>
                        <td>{getMimeExtension(doc.mime_type)}</td>
                        <td>{formatDate(doc.created_at)}</td>
                        <td>
                          {/* Show intelligent job status based on document status */}
                          {doc.status === 'processed' && doc.persist_failed ? (
                            <span className="job-badge failed" title="Build completed but graph was not saved to database">⚠ persist failed</span>
                          ) : doc.status === 'processed' ? (
                            <span className="job-badge completed">✓ completed</span>
                          ) : doc.status === 'processing' ? (
                            <span className="job-badge processing">⏳ processing</span>
                          ) : doc.status === 'failed' ? (
                            <span className="job-badge failed">✗ failed</span>
                          ) : doc.job_status ? (
                            <span className={`job-badge ${doc.job_status}`}>
                              {doc.job_status}
                              {doc.job_attempt > 1 && ` (attempt ${doc.job_attempt})`}
                            </span>
                          ) : null}
                        </td>
                        <td className="error-cell">
                          {doc.error_message && (
                            <div className="error-details">
                              <span className="error-message" title={doc.error_message}>
                                {doc.error_message.substring(0, 80)}
                                {doc.error_message.length > 80 && '...'}
                              </span>
                              {/* Show chunk failure indicator if error mentions chunk */}
                              {doc.error_message.toLowerCase().includes('chunk') && (
                                <div className="chunk-failure-details">
                                  <span className="chunk-failure-icon">❌</span>
                                  <span className="chunk-failure-text">
                                    Chunk processing failed after 3 retries
                                  </span>
                                  {doc.has_output && (
                                    <button 
                                      className="btn-link view-chunk-details"
                                      onClick={() => handleShowOutput(doc.document_id)}
                                    >
                                      View chunk details
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        {/* Actions column removed - buttons are now in the toolbar row above */}
                      </tr>
                      {/* Per-document settings row.
                          Hidden for FGE: composite-only knobs that the precision pipeline
                          does not consume. */}
                      {!isFge && isExpanded && (
                        <tr className="document-params-row">
                          <td colSpan="8">
                            <div className="document-params-content">
                              <div className="params-grid">
                                <div className="param-item">
                                  <label>Model</label>
                                  <select
                                    value={docParams.model || 'gpt-4o'}
                                    onChange={(e) => handleUpdateDocParams(doc.document_id, { model: e.target.value })}
                                    disabled={!canEdit}
                                  >
                                    {modelOptions.map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="param-item">
                                  <label>Chunking Strategy</label>
                                  <select
                                    value={docParams.chunking_strategy || 'word_based'}
                                    onChange={(e) => handleUpdateDocParams(doc.document_id, { 
                                      chunking_strategy: e.target.value 
                                    })}
                                    disabled={!canEdit}
                                    className="chunking-strategy-select"
                                  >
                                    {CHUNKING_STRATEGY_OPTIONS.map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="param-item">
                                  <label>Max Iterations</label>
                                  <input
                                    type="number"
                                    min="1"
                                    max="5"
                                    value={docParams.max_iterations_per_chunk || 2}
                                    onChange={(e) => handleUpdateDocParams(doc.document_id, { 
                                      max_iterations_per_chunk: parseInt(e.target.value) || 2 
                                    })}
                                    disabled={!canEdit}
                                  />
                                </div>
                                <div className="param-item">
                                  <label>Chunk Retries</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="5"
                                    value={docParams.chunk_retries || 2}
                                    onChange={(e) => handleUpdateDocParams(doc.document_id, { 
                                      chunk_retries: parseInt(e.target.value) || 2 
                                    })}
                                    disabled={!canEdit}
                                  />
                                </div>
                                {/* Coverage Validation Settings */}
                                <div className="param-item checkbox-item">
                                  <label>
                                    <input
                                      type="checkbox"
                                      checked={docParams.enable_coverage_validation !== false}
                                      onChange={(e) => handleUpdateDocParams(doc.document_id, { 
                                        enable_coverage_validation: e.target.checked 
                                      })}
                                      disabled={!canEdit}
                                    />
                                    Coverage
                                  </label>
                                </div>
                                <div className="param-item">
                                  <label>Min Coverage %</label>
                                  <input
                                    type="number"
                                    min="50"
                                    max="95"
                                    step="5"
                                    value={docParams.min_coverage_threshold || 70}
                                    onChange={(e) => handleUpdateDocParams(doc.document_id, { 
                                      min_coverage_threshold: parseInt(e.target.value) || 70 
                                    })}
                                    disabled={!canEdit || docParams.enable_coverage_validation === false}
                                  />
                                </div>
                                <div className="param-item checkbox-item">
                                  <label>
                                    <input
                                      type="checkbox"
                                      checked={docParams.enable_reextraction !== false}
                                      onChange={(e) => handleUpdateDocParams(doc.document_id, { 
                                        enable_reextraction: e.target.checked 
                                      })}
                                      disabled={!canEdit || docParams.enable_coverage_validation === false}
                                    />
                                    Re-extract
                                  </label>
                                </div>
                              </div>
                              {!canEdit && (
                                <p className="params-note">Parameters cannot be changed after processing has started.</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {processStatus.message && (
              <div className={`process-status ${processStatus.isError ? 'error' : ''}`}>
                {processStatus.message}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Output Modal */}
      {showOutputModal && (
        <div className="output-modal-overlay" onClick={closeOutputModal}>
          <div className="output-modal" onClick={(e) => e.stopPropagation()}>
            <div className="output-modal-header">
              <h2>Processing Output</h2>
              <button className="modal-close" onClick={closeOutputModal}>&times;</button>
            </div>
            <div className="output-modal-content">
              {loadingOutput ? (
                <div className="loading-output">
                  <div className="loading-spinner"></div>
                  <p>Loading output...</p>
                </div>
              ) : selectedOutput?.error ? (
                <div className="output-error">
                  <p>{selectedOutput.error}</p>
                </div>
              ) : selectedOutput ? (
                <>
                  {/* Summary Stats */}
                  <div className="output-summary">
                    <h3>Summary</h3>
                    <div className="summary-grid">
                      <div className="summary-item">
                        <span className="label">Chunks Processed</span>
                        <span className="value">{selectedOutput.chunks_processed || 0}</span>
                      </div>
                      <div className="summary-item">
                        <span className="label">Total Nodes</span>
                        <span className="value">{selectedOutput.total_nodes || 0}</span>
                      </div>
                      <div className="summary-item">
                        <span className="label">Total Synapses</span>
                        <span className="value">{selectedOutput.total_synapses || 0}</span>
                      </div>
                      <div className="summary-item">
                        <span className="label">Total Gates</span>
                        <span className="value">{selectedOutput.total_gates || 0}</span>
                      </div>
                      <div className="summary-item">
                        <span className="label">Merges Performed</span>
                        <span className="value">{selectedOutput.merges_performed || 0}</span>
                      </div>
                      <div className="summary-item">
                        <span className="label">Model Used</span>
                        <span className="value">{selectedOutput.model_used || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Q&A Results */}
                  {selectedOutput.qa_results_all && selectedOutput.qa_results_all.length > 0 && (
                    <div className="output-qa">
                      <h3>Q&A Test Results ({selectedOutput.qa_results_all.length})</h3>
                      <div className="qa-summary">
                        <span className="qa-pass">
                          Passed: {selectedOutput.qa_results_all.filter(q => q.pass).length}
                        </span>
                        <span className="qa-fail">
                          Failed: {selectedOutput.qa_results_all.filter(q => !q.pass).length}
                        </span>
                      </div>
                      <div className="qa-list">
                        {selectedOutput.qa_results_all.slice(0, 10).map((qa, idx) => (
                          <div key={idx} className={`qa-item ${qa.pass ? 'pass' : 'fail'}`}>
                            <div className="qa-question"><strong>Q:</strong> {qa.question}</div>
                            <div className="qa-answer"><strong>Expected:</strong> {qa.expected_answer}</div>
                            <div className="qa-result"><strong>Got:</strong> {qa.graph_answer}</div>
                          </div>
                        ))}
                        {selectedOutput.qa_results_all.length > 10 && (
                          <p className="qa-more">... and {selectedOutput.qa_results_all.length - 10} more</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Persistence Info */}
                  {selectedOutput.persistence && (
                    <div className="output-persistence">
                      <h3>Persistence Details</h3>
                      <div className="persistence-info">
                        <p><strong>Graph ID:</strong> {selectedOutput.persistence.graph_id}</p>
                        <p><strong>Nodes Persisted:</strong> {selectedOutput.persistence.node_count}</p>
                        <p><strong>Synapses Persisted:</strong> {selectedOutput.persistence.synapse_count}</p>
                        {selectedOutput.persistence.qa_count > 0 && (
                          <p><strong>Q&A Pairs:</strong> {selectedOutput.persistence.qa_count}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Full JSON (collapsible) */}
                  <details className="output-json-details">
                    <summary>View Full JSON Response</summary>
                    <pre className="output-json">
                      {JSON.stringify(selectedOutput, null, 2)}
                    </pre>
                    <button 
                      className="btn-secondary copy-json"
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(selectedOutput, null, 2));
                        setProcessStatus({ message: 'JSON copied to clipboard', isError: false });
                      }}
                    >
                      Copy JSON
                    </button>
                  </details>
                </>
              ) : (
                <p>No output data available.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Consolidation History Details Modal */}
      {showConsolidationDetails && (
        <div className="consolidation-details-modal-overlay" onClick={() => setShowConsolidationDetails(false)}>
          <div className="consolidation-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Consolidation History</h2>
              <button className="modal-close" onClick={() => setShowConsolidationDetails(false)}>&times;</button>
            </div>
            <div className="modal-content">
              {consolidationHistory.length === 0 ? (
                <div className="empty-state">
                  <p>No consolidation history available.</p>
                </div>
              ) : (
                <div className="history-list">
                  {consolidationHistory.map((record, idx) => (
                    <div key={record.consolidation_id} className={`history-record ${idx === 0 ? 'latest' : ''}`}>
                      <div className="record-header">
                        <span className={`status-badge status-${record.status}`}>
                          {record.status === 'completed' ? '✓ ' : record.status === 'failed' ? '✗ ' : ''}
                          {record.status.toUpperCase()}
                        </span>
                        <span className="record-id">ID: {record.consolidation_id.substring(0, 8)}...</span>
                        {idx === 0 && <span className="latest-badge">LATEST</span>}
                      </div>
                      <div className="record-dates">
                        <div><strong>Started:</strong> {new Date(record.started_at).toLocaleString()}</div>
                        {record.completed_at && (
                          <div><strong>Completed:</strong> {new Date(record.completed_at).toLocaleString()}</div>
                        )}
                      </div>
                      {record.error_message && (
                        <div className="record-error">
                          <strong>Error:</strong> {record.error_message}
                        </div>
                      )}
                      {record.summary && (
                        <div className="record-summary">
                          <h4>Summary</h4>
                          <div className="summary-details">
                            {/* Show message if present (e.g., "No consolidation candidates found") */}
                            {record.summary.message && (
                              <div className="summary-message">
                                <span className="message-icon">ℹ️</span>
                                <span>{record.summary.message}</span>
                              </div>
                            )}
                            
                            {/* Show incremental mode info */}
                            {record.summary.incremental_mode && (
                              <div className="summary-mode incremental">
                                <span className="mode-icon">🔄</span>
                                <span>
                                  Incremental consolidation
                                  {record.summary.selected_document_count && (
                                    <> ({record.summary.selected_document_count} selected document{record.summary.selected_document_count > 1 ? 's' : ''})</>
                                  )}
                                </span>
                              </div>
                            )}
                            
                            {/* Show total nodes analyzed */}
                            {record.summary.total_nodes && (
                              <div className="summary-stat">
                                <span className="stat-label">Total nodes analyzed:</span>
                                <span className="stat-value">{record.summary.total_nodes}</span>
                              </div>
                            )}
                            
                            {/* Show total candidates found */}
                            {record.summary.total_candidates !== undefined && (
                              <div className="summary-stat">
                                <span className="stat-label">Candidates found:</span>
                                <span className="stat-value">{record.summary.total_candidates}</span>
                              </div>
                            )}
                            
                            {record.summary.auto_applied && (
                              <div className="summary-category auto">
                                <span className="category-icon">✓</span>
                                <div className="category-content">
                                  <strong>Auto-Applied (High Confidence ≥85%)</strong>
                                  <ul>
                                    <li>{record.summary.auto_applied.merges || 0} node merges</li>
                                    <li>{record.summary.auto_applied.relationships || 0} relationships</li>
                                    <li>{record.summary.auto_applied.gates || 0} gates</li>
                                  </ul>
                                </div>
                              </div>
                            )}
                            {record.summary.applied_with_warning && (
                              <div className="summary-category warning">
                                <span className="category-icon">⚠️</span>
                                <div className="category-content">
                                  <strong>Applied with Warning (70-84%)</strong>
                                  <ul>
                                    <li>{record.summary.applied_with_warning.merges || 0} node merges</li>
                                    <li>{record.summary.applied_with_warning.relationships || 0} relationships</li>
                                    <li>{record.summary.applied_with_warning.gates || 0} gates</li>
                                  </ul>
                                </div>
                              </div>
                            )}
                            {record.summary.queued_for_review && (
                              <div className="summary-category review">
                                <span className="category-icon">🔍</span>
                                <div className="category-content">
                                  <strong>Queued for Human Review (&lt;70%)</strong>
                                  <ul>
                                    <li>{record.summary.queued_for_review.merges || 0} node merges</li>
                                    <li>{record.summary.queued_for_review.relationships || 0} relationships</li>
                                    <li>{record.summary.queued_for_review.gates || 0} gates</li>
                                  </ul>
                                </div>
                              </div>
                            )}
                            {record.summary.candidates && (
                              <div className="summary-category candidates">
                                <span className="category-icon">📊</span>
                                <div className="category-content">
                                  <strong>Candidates Analyzed</strong>
                                  <ul>
                                    <li>{record.summary.candidates.merges || 0} potential merges</li>
                                    <li>{record.summary.candidates.relationships || 0} potential relationships</li>
                                    <li>{record.summary.candidates.gates || 0} potential gates</li>
                                  </ul>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowConsolidationDetails(false)}>
                Close
              </button>
              {reviewQueueCount > 0 && (
                <button 
                  className="btn-primary"
                  onClick={() => { setShowConsolidationDetails(false); setShowReviewModal(true); loadReviewItems(); }}
                >
                  Review Pending Items ({reviewQueueCount})
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Review Queue Modal */}
      {showReviewModal && (
        <div className="review-modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()}>
            <div className="review-modal-header">
              <h2>Review Queue ({reviewItems.length} items)</h2>
              <button className="modal-close" onClick={() => setShowReviewModal(false)}>&times;</button>
            </div>
            <div className="review-modal-content">
              {loadingReview ? (
                <div className="loading-review">
                  <div className="loading-spinner"></div>
                  <p>Loading review items...</p>
                </div>
              ) : reviewItems.length === 0 ? (
                <div className="empty-review">
                  <p>No items pending review.</p>
                </div>
              ) : (
                <div className="review-items-list">
                  {reviewItems.map((item) => (
                    <div key={item.review_id} className="review-item">
                      <div className="review-item-header">
                        <span className={`review-type ${item.candidate_type}`}>
                          {item.candidate_type.toUpperCase()}
                        </span>
                        <span className="review-confidence">
                          Confidence: {(item.final_confidence * 100).toFixed(1)}%
                        </span>
                      </div>
                      <div className="review-item-body">
                        {item.candidate_type === 'merge' && item.candidate_data && (
                          <>
                            <p><strong>Merge:</strong> "{item.candidate_data.canonical_node?.label}" 
                               with "{item.candidate_data.duplicate_nodes?.[0]?.label}"</p>
                          </>
                        )}
                        {item.candidate_type === 'relationship' && item.candidate_data && (
                          <>
                            <p><strong>Relationship:</strong> "{item.candidate_data.source_node?.label}" 
                               → "{item.candidate_data.target_node?.label}"</p>
                            <p><em>Type: {item.candidate_data.relation_type}</em></p>
                          </>
                        )}
                        {item.candidate_type === 'gate' && item.candidate_data && (
                          <>
                            <p><strong>{item.candidate_data.gate_type} Gate</strong></p>
                            <p><em>{item.candidate_data.condition_description}</em></p>
                          </>
                        )}
                      </div>
                      <div className="review-item-reasoning">
                        <details>
                          <summary>LLM Reasoning ({item.llm_scores?.length || 0} passes)</summary>
                          <div className="reasoning-list">
                            {item.llm_reasoning?.map((reason, idx) => (
                              <div key={idx} className="reasoning-item">
                                <strong>Pass {idx + 1} (Score: {(item.llm_scores?.[idx] * 100 || 0).toFixed(0)}%):</strong>
                                <p>{reason}</p>
                              </div>
                            ))}
                          </div>
                        </details>
                      </div>
                      <div className="review-item-actions">
                        <button 
                          className="btn-primary btn-sm"
                          onClick={() => handleReviewAction(item.review_id, 'approve')}
                        >
                          Approve
                        </button>
                        <button 
                          className="btn-secondary btn-sm"
                          onClick={() => handleReviewAction(item.review_id, 'reject')}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Graph Prompts Modal */}
      <GraphPromptsModal
        isOpen={showPromptsModal}
        onClose={() => setShowPromptsModal(false)}
        graphId={graphId}
        documents={documents}
        onPromptsUpdated={loadPromptsStatus}
      />

      {/* Chunk Management Modal */}
      <ChunkModal
        isOpen={showChunkModal}
        onClose={() => setShowChunkModal(false)}
        graphId={graphId}
        onChunksUpdated={handleChunksUpdated}
      />

      {/* Reprocess Confirmation Modal */}
      <ReprocessConfirmModal
        isOpen={showReprocessModal}
        onClose={() => setShowReprocessModal(false)}
        onConfirm={handleReprocessGraph}
        loading={reprocessing}
        graphName={graph?.graphName || 'this graph'}
      />

      {/* Document Prompt View/Edit Modal */}
      <DocumentPromptModal
        isOpen={!!promptModalDocId}
        onClose={() => setPromptModalDocId(null)}
        documentId={promptModalDocId}
        documentName={promptModalDocName}
        onPromptSaved={() => loadDocuments()}
        onPromptDeleted={() => loadDocuments()}
      />
    </div>
  );
}

export default BuildGraphPage;
